import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Delete02Icon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useGetOrderItemsQuery, useUpdateOrderItemDrawingMutation } from '@/app/services/orderItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

import DeleteLayerAlertDialog from '../DeleteLayerAlertDialog'

import LayerName from './LayerName'
import LayerType from './LayerType'

const classes = {
    colourAdornment: {
        width: 16,
        height: 16,
        borderRadius: 1,
        padding: 1,
    },
    icon: {
        color: 'action.active',
    },
}

/**
 * Generates a deterministic layer ID based on layer name
 * Creates stable IDs across renders for consistent React reconciliation
 * @param {string} layerName - The layer name to generate ID from
 * @returns {string} Deterministic layer ID (e.g., "layer-steel-304")
 */
const generateLayerId = (layerName) => {
    // Create deterministic ID from layer name (stable across renders)
    return `layer-${layerName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')}`
}

/**
 * Generates a unique key for a path within an order item
 * @param {Object} orderItem - Order item object
 * @param {Object} path - Path object
 * @returns {string} Unique path key
 */
const getPartPathKey = (orderItem, path) => {
    return `${orderItem.partId}-${path.pathId}`
}

const Layers = ({ drawing, orderItemId }) => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const { enqueueSnackbar } = useSnackbar()

    const organisationId = useSelector(selectOrganisationId)
    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [updateOrderItemDrawing, { isLoading: isUpdating }] = useUpdateOrderItemDrawingMutation({
        fixedCacheKey: 'updateOrderItemDrawing',
    })

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const [layerIdToDelete, setLayerIdToDelete] = useState(null)

    const handleUpdateOrderItemDrawing = async (orderItemDrawing) => {
        try {
            const orderItemId = orderItem.id
            const orderId = orderItem.quoteId
            orderItemDrawing.paths = Object.values(orderItemDrawing.paths)
            const drawing = { ...orderItemDrawing }
            await updateOrderItemDrawing({
                organisationId,
                orderId,
                orderItemId,
                drawing,
            }).unwrap()

            enqueueSnackbar(t('order item updated'), { variant: 'success' })
        } catch {
            enqueueSnackbar(t('Failed to update the order item drawing'), {
                variant: 'error',
            })
        }
    }

    const removeLayerFromPart = (layerId) => {
        const updatedDrawingPaths = []

        if (drawing?.paths) {
            Object.values(drawing.paths).forEach((path) => {
                const pathKey = getPartPathKey(orderItem, path)

                if (pathToLayersMap[pathKey] !== layerId) {
                    updatedDrawingPaths.push(path)
                }
            })
        }

        // Create immutable copy instead of mutating prop
        return {
            ...drawing,
            paths: updatedDrawingPaths,
        }
    }

    const handleLayerDeleteConfirm = () => {
        const updatedDrawing = removeLayerFromPart(layerIdToDelete)

        setLayerIdToDelete(null)

        handleUpdateOrderItemDrawing(updatedDrawing)
    }

    const handleLayerDeleteClick = (layerId) => {
        setLayerIdToDelete(layerId)
    }

    const compareLayerNames = (a, b) => {
        if (a[1].name.toLowerCase() < b[1].name.toLowerCase()) {
            return -1
        }
        if (a[1].name.toLowerCase() > b[1].name.toLowerCase()) {
            return 1
        }
        return 0
    }

    // Derive layer mappings from drawing and orderItem using useMemo
    const { layersMap, nameToLayersMap, pathToLayersMap } = useMemo(() => {
        // Guard against null values and verify paths is an object
        if (!drawing?.paths || !orderItem || typeof drawing.paths !== 'object' || Array.isArray(drawing.paths)) {
            return {
                layersMap: {},
                nameToLayersMap: {},
                pathToLayersMap: {},
            }
        }

        const newLayersMap = {}
        const newPathToLayersMap = {}
        const newNameToLayersMap = {}

        Object.values(drawing.paths).forEach((path) => {
            // Defensive check: verify path has layer with name
            if (!path?.layer?.name) {
                console.warn('DrawingDoctor: Path missing layer or layer name, skipping:', path)
                return // Skip this path
            }

            const layer = path.layer
            let layerId = newNameToLayersMap[layer.name]

            if (!layerId) {
                // Generate deterministic ID based on layer name (stable across renders)
                layerId = generateLayerId(layer.name)

                newNameToLayersMap[layer.name] = layerId
                newLayersMap[layerId] = layer
            }
            const pathKey = getPartPathKey(orderItem, path)
            newPathToLayersMap[pathKey] = layerId
        })

        return {
            layersMap: newLayersMap,
            nameToLayersMap: newNameToLayersMap,
            pathToLayersMap: newPathToLayersMap,
        }
    }, [drawing, orderItem])

    const updateLayersInPart = (layerId, propertyName, newValue) => {
        if (!drawing?.paths) {
            return drawing
        }

        // Get the current layer and create updated version
        const currentLayer = layersMap[layerId]
        const updatedLayer = { ...currentLayer, [propertyName]: newValue }

        // Create immutable copy with updated paths
        const updatedPaths = Object.values(drawing.paths).map((path) => {
            const pathKey = getPartPathKey(orderItem, path)

            if (pathToLayersMap[pathKey] === layerId) {
                // Create new path with updated layer
                return {
                    ...path,
                    layer: { ...updatedLayer },
                    entities: path.entities.map((entity) => ({
                        ...entity,
                        layer: { ...updatedLayer },
                    })),
                }
            }

            return path
        })

        // Return immutable copy instead of mutating prop
        return {
            ...drawing,
            paths: updatedPaths,
        }
    }

    const handleLayerTypeChange = (newType, layerId) => {
        // Update layer type in drawing
        const updatedDrawing = updateLayersInPart(layerId, 'type', newType)

        handleUpdateOrderItemDrawing(updatedDrawing)
    }

    const handleLayerNameChange = (newName, layerId) => {
        if (!newName) {
            enqueueSnackbar(t('Layer name is required'), {
                variant: 'error',
            })
        } else if (nameToLayersMap[newName]) {
            enqueueSnackbar(t('Layer names must be unique'), {
                variant: 'error',
            })
        } else {
            // Update layer name in drawing
            const updatedDrawing = updateLayersInPart(layerId, 'name', newName)

            handleUpdateOrderItemDrawing(updatedDrawing)
        }
    }

    const layers = Object.entries(layersMap).sort((a, b) => compareLayerNames(a, b))

    return (
        <Box>
            {layers.map(([layerId, layer], _index) => (
                <Box
                    key={layerId}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'space-between',
                        margin: 1,
                    }}
                >
                    <Box
                        style={
                            !layer.colour || layer.colour.toLowerCase() === 'none'
                                ? { border: '0.5px dashed black' }
                                : { backgroundColor: layer.colour.toLowerCase() }
                        }
                        sx={classes.colourAdornment}
                    />
                    <TbxTooltip
                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                        arrow
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <LayerName
                                handleUpdateLayerName={(newName) => {
                                    handleLayerNameChange(newName, layerId)
                                }}
                                isDisabled={isUpdating || isPartFromLibrary}
                                layerName={layer.name}
                            />
                        </Box>
                    </TbxTooltip>
                    <TbxTooltip
                        title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                        arrow
                    >
                        <Box
                            sx={{
                                flex: 1,
                            }}
                        >
                            <LayerType
                                handleUpdateLayerType={(newType) => {
                                    handleLayerTypeChange(newType, layerId)
                                }}
                                isDisabled={isUpdating || isPartFromLibrary}
                                layerType={layer.type}
                            />
                        </Box>
                    </TbxTooltip>
                    {layers.length > 1 && !isPartFromLibrary ? (
                        <IconButton
                            disabled={isUpdating}
                            size="small"
                            sx={classes.icon}
                            onClick={() => handleLayerDeleteClick(layerId)}
                        >
                            <HugeiconsIcon
                                icon={Delete02Icon}
                                size={18}
                            />
                        </IconButton>
                    ) : null}
                </Box>
            ))}
            <DeleteLayerAlertDialog
                layerName={layerIdToDelete ? layersMap[layerIdToDelete].name : ''}
                open={Boolean(layerIdToDelete)}
                onCancelClose={() => setLayerIdToDelete(null)}
                onOkClose={handleLayerDeleteConfirm}
            />
        </Box>
    )
}

Layers.propTypes = {
    drawing: PropTypes.object,
    orderItemId: PropTypes.string,
}

export default Layers
